import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "common/utils/strings";
import { useRequestPasswordResetMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { spacing } from "../../../stitches.config";
import {
  ContentSection,
  HeaderSection,
  UpdatedContainer,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Button";
import { Section } from "../../Common/Layout";
import { Body, Title } from "../../Common/Typography";
import { Text } from "../../Inputs/react-hook-form";

const SuccessMessage = () => {
  const { t } = useTranslation();
  return (
    <Body size="default" type="regular">
      {t("password-reset-request-success-message")}
    </Body>
  );
};

interface FormStructure {
  email: string;
}

export const RequestPasswordResetModalContent = () => {
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();

  const [completed, setCompleted] = useState(false);

  const [requestReset, { loading }] = useRequestPasswordResetMutation({
    onCompleted: () => {
      setCompleted(true);
    },
    onError: error => {
      if (error.graphQLErrors[0]?.extensions?.code === "USER_ERROR") {
        addErrorToast(error.message);
      } else {
        addErrorToast(t("password-reset-request-error-message"));
      }
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormStructure>({});

  const onSubmit = async (data: FormStructure) => {
    await requestReset({
      variables: { email: data.email },
    });
  };

  return (
    <UpdatedContainer size="small">
      <HeaderSection>
        <Title size="large" type="semiBold">
          {completed ? t("success-title") : t("password-reset-request-title")}
        </Title>
      </HeaderSection>
      <ContentSection>
        {completed ? (
          <Section>
            <SuccessMessage />
          </Section>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Section>
              <Body size="default" type="regular">
                {t("password-reset-request-subtitle")}
              </Body>
              <Text
                label={t("sign-in-email")}
                type="email"
                data-testid="email"
                error={errors.email?.message}
                {...register("email", {
                  validate: val => {
                    if (!isValidEmail(val)) {
                      return t("signup-email-invalid");
                    }
                    return;
                  },
                })}
              />
              <div style={{ marginTop: spacing.m.toString() }}>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  type="submit"
                  styleVariant="primary"
                  size="medium"
                  disabled={loading}
                  stretch
                  data-testid="password-reset-request-submit"
                >
                  {t("submit")}
                </Button>
              </div>
            </Section>
          </form>
        )}
      </ContentSection>
    </UpdatedContainer>
  );
};
